/**
 * 静止盤面（レーン格子・待機中の矢印）を焼き込む RenderTexture の解像度を決める。
 *
 * 焼き込み層はワールド座標 1 単位あたり 1 テクセルで作られていたため、盤面が
 * 小さいほどカメラが拡大され、実機（devicePixelRatio 2〜3）ではテクセルが
 * 数物理ピクセルに引き伸ばされて矢印の縁がにじんでいた。カメラ倍率と同じだけ
 * テクセルを持たせれば、焼き込んだ矢印も直接描画と同じ鮮明さになる。
 */

/** これ以上細かくしても実機で差が出ず、GPU メモリだけ増えるので打ち止め。 */
export const maxBoardLayerScale = 3;
/**
 * GPU が申告する上限とは別に置く自前の上限。36x50 盤面（1800x2500 ワールド）を
 * 2 層ぶん抱えるので、テクスチャ 1 辺をここで抑えてメモリの暴走を防ぐ。
 */
export const boardTextureSizeCap = 4096;
/**
 * カメラ倍率に対して持たせる上乗せ。ちょうど等倍だと、焼き込み層のテクセル格子と
 * 画面の物理ピクセル格子が半テクセルずれた瞬間に線がにじむ。1.35 倍持たせて
 * 実質的なスーパーサンプルにする。
 */
export const boardLayerHeadroom = 1.35;
/**
 * 縮小率の下限。36x50 盤面のように画面へ収めるだけで大きく縮む盤面では、
 * 等倍で焼くとテクスチャ側が細かすぎて GPU の縮小サンプリングから
 * レーン格子の細線が抜け落ちる（実機で格子がまだらに消えていた）。
 * 表示サイズに見合った解像度まで落として焼くほうが、見た目も消費メモリも良い。
 */
export const minBoardLayerScale = 0.3;

export interface BoardLayerScaleOptions {
  /** 今のカメラ倍率（ワールド 1 単位あたりの物理ピクセル数）。 */
  readonly cameraZoom: number;
  readonly layerWidth: number;
  readonly layerHeight: number;
  /** WebGL の MAX_TEXTURE_SIZE。取得できない環境では上限だけで判断する。 */
  readonly maxTextureSize: number;
}

export function resolveBoardLayerScale(options: BoardLayerScaleOptions): number {
  const limit = Math.max(1, Math.min(options.maxTextureSize, boardTextureSizeCap));
  const longestSide = Math.max(options.layerWidth, options.layerHeight, 1);
  const ceiling = Math.min(maxBoardLayerScale, limit / longestSide);
  const target = Math.max(options.cameraZoom * boardLayerHeadroom, minBoardLayerScale);
  return Math.min(target, ceiling);
}

/**
 * ワールド寸法から実際に確保するテクスチャの辺の長さ。
 *
 * 切り上げないのは上限ぴったりの盤面（36x50 を最大ズーム）で 1 テクセルはみ出し、
 * GPU の MAX_TEXTURE_SIZE をちょうど超えてしまうため。偶数へ落としているのは、
 * Phaser の DynamicTexture が奇数寸法を偶数へ切り上げる（forceEven）ので、
 * そこでも上限を越えさせないため。
 */
export function boardTextureSize(worldLength: number, scale: number): number {
  return Math.max(2, Math.floor(worldLength * scale / 2) * 2);
}

/**
 * 焼き直すかどうか。ピンチのたびに 2500px 級のテクスチャを作り直すと
 * コマ落ちするので、粗くなる側は敏感に・細かすぎる側は鈍感に判定する。
 */
export function shouldRebakeBoardLayers(currentScale: number, desiredScale: number): boolean {
  if (currentScale <= 0) {
    return true;
  }
  return desiredScale > currentScale * 1.12 || desiredScale < currentScale / 1.6;
}
